import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import { cn } from '@shared/utils';

dayjs.locale('pt-br');

export interface DateRange {
  from?: string | undefined;
  to?: string | undefined;
}

export interface DateRangePickerProps {
  readonly id?: string;
  readonly label?: string;
  readonly value?: DateRange;
  readonly onChange?: (range: DateRange) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly className?: string;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function formatDisplay(range: DateRange | undefined): string {
  if (!range?.from && !range?.to) return '';
  const from = range.from ? dayjs(range.from).format('DD/MM/YYYY') : '';
  const to = range.to ? dayjs(range.to).format('DD/MM/YYYY') : '';
  if (from && to) return `${from} — ${to}`;
  if (from) return `${from} —`;
  return '';
}

function getRangeDayClass(isEndpoint: boolean, isInRange: boolean, isToday: boolean): string {
  if (isEndpoint) return 'bg-[#003B6A] text-white';
  if (isInRange) return 'bg-[#003B6A]/10 text-[#003B6A]';
  if (isToday) return 'border border-[#003B6A] text-[#003B6A] hover:bg-[#003B6A]/10';
  return 'text-login-text-secondary hover:bg-[#F3F4F6]';
}

export function DateRangePicker({
  id,
  label,
  value,
  onChange,
  placeholder = 'Selecione o período',
  disabled,
  className,
}: DateRangePickerProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value?.from) return dayjs(value.from);
    return dayjs();
  });
  const [selecting, setSelecting] = useState<'from' | 'to'>('from');
  const [localRange, setLocalRange] = useState<DateRange>(value ?? {});

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local range with external value change is intentional
    setLocalRange(value ?? {});
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectDate = useCallback(
    (date: dayjs.Dayjs) => {
      const dateStr = date.format('YYYY-MM-DD');
      if (selecting === 'from') {
        const newRange: DateRange = { from: dateStr, to: localRange.to };
        if (newRange.to && dateStr > newRange.to) {
          newRange.to = undefined;
        }
        setLocalRange(newRange);
        setSelecting('to');
        onChange?.(newRange);
      } else {
        let newRange: DateRange;
        if (localRange.from && dateStr < localRange.from) {
          newRange = { from: dateStr, to: localRange.from };
        } else {
          newRange = { from: localRange.from, to: dateStr };
        }
        setLocalRange(newRange);
        setSelecting('from');
        onChange?.(newRange);
        setIsOpen(false);
      }
    },
    [selecting, localRange, onChange],
  );

  const prevMonth = () => setViewDate(viewDate.subtract(1, 'month'));
  const nextMonth = () => setViewDate(viewDate.add(1, 'month'));

  const daysInMonth = viewDate.daysInMonth();
  const firstDayOfWeek = viewDate.startOf('month').day();
  const days: (dayjs.Dayjs | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(viewDate.date(d));

  const fromDate = localRange.from ? dayjs(localRange.from) : null;
  const toDate = localRange.to ? dayjs(localRange.to) : null;

  return (
    <div ref={containerRef} className={cn('relative flex flex-col gap-1.5', className)}>
      {label !== undefined ? (
        <label htmlFor={id} className="text-xs font-semibold text-login-text-secondary">
          {label}
        </label>
      ) : null}

      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen(!isOpen);
        }}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border bg-white px-3',
          'text-sm text-login-text-secondary',
          'hover:border-login-border-brand focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'transition-colors border-login-border-primary',
        )}
      >
        <span className={localRange.from ? '' : 'text-[#A4A7AE]'}>
          {formatDisplay(localRange) || placeholder}
        </span>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-[#71767F]"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {isOpen ? (
        <div className="absolute top-full right-0 z-50 mt-1 w-72 rounded-lg border border-login-border-primary bg-white p-4 shadow-lg">
          {/* Selecting indicator */}
          <div className="mb-3 flex items-center gap-2 text-xs text-[#71767F]">
            <span
              className={cn(
                'rounded px-2 py-0.5',
                selecting === 'from' ? 'bg-[#003B6A] text-white' : 'bg-[#F3F4F6]',
              )}
            >
              De: {localRange.from ? dayjs(localRange.from).format('DD/MM') : '—'}
            </span>
            <span
              className={cn(
                'rounded px-2 py-0.5',
                selecting === 'to' ? 'bg-[#003B6A] text-white' : 'bg-[#F3F4F6]',
              )}
            >
              Até: {localRange.to ? dayjs(localRange.to).format('DD/MM') : '—'}
            </span>
          </div>

          {/* Month navigation */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={prevMonth}
              className="flex h-7 w-7 items-center justify-center rounded-md text-login-text-secondary hover:bg-[#F3F4F6]"
              aria-label="Mês anterior"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-login-text-secondary capitalize">
              {viewDate.format('MMMM YYYY')}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="flex h-7 w-7 items-center justify-center rounded-md text-login-text-secondary hover:bg-[#F3F4F6]"
              aria-label="Próximo mês"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAYS.map((day) => (
              <span key={day} className="py-1 text-[10px] font-semibold text-[#71767F]">
                {day}
              </span>
            ))}
            {days.map((day, idx) => {
              if (day === null) return <span key={`empty-${String(idx)}`} />;
              const dateStr = day.format('YYYY-MM-DD');
              const isFrom = fromDate !== null && day.isSame(fromDate, 'day');
              const isTo = toDate !== null && day.isSame(toDate, 'day');
              const isInRange =
                fromDate !== null &&
                toDate !== null &&
                day.isAfter(fromDate, 'day') &&
                day.isBefore(toDate, 'day');
              const isToday = day.isSame(dayjs(), 'day');

              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => selectDate(day)}
                  className={cn(
                    'h-8 w-8 rounded-md text-xs font-medium transition-colors',
                    getRangeDayClass(isFrom || isTo, isInRange, isToday),
                  )}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
